import React from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import {
  EllipsisVerticalIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  EyeIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface EventActionMenuProps {
  eventId: number;
  eventUuid: string;
  onDuplicateEvent: (eventId: number) => void;
  onEditEvent: (uuid: string) => void;
  onDeleteEvent: (id: number) => void;
  onViewSessions: (uuid: string) => void;
}

const EventActionMenu: React.FC<EventActionMenuProps> = ({
  eventId,
  eventUuid,
  onDuplicateEvent,
  onEditEvent,
  onDeleteEvent,
  onViewSessions,
}) => {
  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

  return (
    <TooltipProvider delayDuration={0}>
      <div className="flex items-center justify-end gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
              onClick={(e) => {
                stopPropagation(e);
                onEditEvent(eventUuid);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <PencilSquareIcon className="h-4 w-4" />
              <span className="sr-only">Edit event</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Edit</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
              onClick={(e) => {
                stopPropagation(e);
                onViewSessions(eventUuid);
              }}
              size="icon"
              type="button"
              variant="ghost"
            >
              <EyeIcon className="h-4 w-4" />
              <span className="sr-only">View sessions</span>
            </Button>
          </TooltipTrigger>
          <TooltipContent>Sessions</TooltipContent>
        </Tooltip>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              className="h-7 w-7 p-0 text-gray-500 hover:text-gray-700 dark:text-slate-400 dark:hover:text-slate-200"
              onClick={stopPropagation}
              size="icon"
              type="button"
              variant="ghost"
            >
              <span className="sr-only">More actions</span>
              <EllipsisVerticalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px] p-2 bg-white">
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-gray-100 transition-colors"
              onClick={() => onDuplicateEvent(eventId)}
            >
              <DocumentDuplicateIcon className="mr-2 h-4 w-4 text-gray-500" />
              Duplicate
            </DropdownMenuItem>
            <DropdownMenuSeparator className="my-1 h-px bg-gray-200" />
            <DropdownMenuItem
              className="flex items-center px-3 py-2 text-sm cursor-pointer hover:bg-red-50 text-red-600 transition-colors"
              onClick={() => onDeleteEvent(eventId)}
            >
              <TrashIcon className="mr-2 h-4 w-4" />
              Delete Event
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </TooltipProvider>
  );
};

export default EventActionMenu;
